import React, { useEffect, useState } from "react";
import { Grid, Box, Paper, Typography, CircularProgress, Chip, IconButton } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import * as actions from "../Common/actions";
import * as releaseActions from "../Releases/actions";
import { toastError, toastSuccess } from "../../utils";
import moment from "moment";
import { Send, Ticket, Calendar, MessageSquare, Paperclip, Download, Trash2, ArrowLeft, XCircle, Pencil, Check, X } from "lucide-react";
import { API_URL } from "../../constants";
import SelectCMP from "react-select";
import darkSelectStyles from "../../theme/reactSelectStyles";
import ActionButton from "../../components/ActionButton";
import StatusBadge from "../../components/StatusBadge";
import CustomShowModal from "../Common/customShowModal";

function ViewTicket(props) {
  const params = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState({});
  const [sendReply, setSendReply] = useState({
    ticket_id: params.id,
    reply: `Dear user,


Hope you found us helpful.

Best regards,
Fronic Media Support Team
Stay Connected with us on Whatsapp - https://fronicmedia.com/wc`,
    attachement: "",
    sent_by: "admin",
  });
  const [sendLoader, setSendLoader] = useState(false);
  const [rejectOptions, setRejectOptions] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: null, data: null });
  const [editingReply, setEditingReply] = useState(null);
  const [editingText, setEditingText] = useState("");

  useEffect(() => {
    if (params.id) {
      fetchTicketsById();
      onGetRejectOptions();
    }
  }, []);

  const onGetRejectOptions = () => {
    releaseActions.ticketReasonsAdmin().then((res) => {
      setRejectOptions(
        Array.isArray(res.body)
          ? res.body.map((item) => ({ label: item.reason, value: item.reason }))
          : []
      );
    }).catch((err) => {
      toastError(err?.message || 'Failed to load ticket reasons');
    });
  };

  const fetchTicketsById = () => {
    props.setLoader(true);
    actions
      .getTicketById(params.id)
      .then((res) => {
        props.setLoader(false);
        setTicket({
          ...res.body,
          attachement: IsJsonString(res.body.attachement)
            ? JSON.parse(res.body.attachement)
            : res.body.attachement,
        });
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Operation failed');
      });
  };

  const IsJsonString = (str) => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  };

  const onSubmitReply = () => {
    if (!sendReply.reply) {
      toastError("Please enter a reply first.");
      return;
    }
    setConfirmModal({ open: true, type: 'reply', data: null });
  };

  const onConfirmReply = () => {
    setConfirmModal({ open: false, type: null, data: null });
    setSendLoader(true);
    actions
      .replyTicket(sendReply)
      .then((res) => {
        setSendLoader(false);
        setSendReply({ ...sendReply, reply: "" });
        fetchTicketsById();
        toastSuccess(res.message || 'Reply sent successfully');
      })
      .catch((err) => {
        setSendLoader(false);
        toastError(err?.message || 'Failed to send reply');
      });
  };

  const onSaveEditReply = (reply_id) => {
    if (!editingText.trim()) {
      toastError("Reply cannot be empty.");
      return;
    }
    props.setLoader(true);
    actions
      .editTicketReplyAdmin({ reply_id, reply: editingText })
      .then((res) => {
        props.setLoader(false);
        setEditingReply(null);
        setEditingText("");
        fetchTicketsById();
        toastSuccess(res.message || 'Reply updated successfully');
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to update reply');
      });
  };

  const keyPressed = (e) => {
    if (e.key === "Enter" && e.shiftKey) return;
    if (e.key === "Enter") {
      e.preventDefault();
      onSubmitReply();
    }
  };

  const onCloseTicket = () => {
    setConfirmModal({ open: true, type: 'close', data: ticket_id });
  };

  const onConfirmClose = () => {
    const id = confirmModal.data;
    setConfirmModal({ open: false, type: null, data: null });
    props.setLoader(true);
    actions
      .closeTicketAdmin({ ticket_id: id })
      .then((res) => {
        props.setLoader(false);
        fetchTicketsById();
        toastSuccess(res.message);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Operation failed');
      });
  };

  const onDelete = (item) => {
    setConfirmModal({ open: true, type: 'delete', data: item });
  };

  const onConfirmDelete = () => {
    const item = confirmModal.data;
    setConfirmModal({ open: false, type: null, data: null });
    props.setLoader(true);
    actions
      .deleteFileFromServer({ fileLink: item })
      .then(() => {
        window.location.reload(false);
      })
      .catch(() => {
        props.setLoader(false);
      });
  };

  const onDeleteReply = (reply_id) => {
    setConfirmModal({ open: true, type: 'deleteReply', data: reply_id });
  };

  const onConfirmDeleteReply = () => {
    const reply_id = confirmModal.data;
    setConfirmModal({ open: false, type: null, data: null });
    props.setLoader(true);
    actions
      .deleteTicketReplyAdmin({ reply_id })
      .then((res) => {
        props.setLoader(false);
        fetchTicketsById();
        toastSuccess(res.message || 'Reply deleted successfully');
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to delete reply');
      });
  };

  const onConfirmAction = () => {
    if (confirmModal.type === 'reply') onConfirmReply();
    else if (confirmModal.type === 'close') onConfirmClose();
    else if (confirmModal.type === 'delete') onConfirmDelete();
    else if (confirmModal.type === 'deleteReply') onConfirmDeleteReply();
  };

  const getConfirmTitle = () => {
    if (confirmModal.type === 'reply') return 'Send Reply';
    if (confirmModal.type === 'close') return 'Close Ticket';
    if (confirmModal.type === 'delete') return 'Delete Attachment';
    if (confirmModal.type === 'deleteReply') return 'Delete Message';
    return '';
  };

  const getConfirmMessage = () => {
    if (confirmModal.type === 'reply') return 'Are you sure you want to send this reply? The user will be notified.';
    if (confirmModal.type === 'close') return 'Are you sure you want to close this ticket? This action cannot be undone.';
    if (confirmModal.type === 'delete') return 'Are you sure you want to delete this attachment? This action cannot be undone.';
    if (confirmModal.type === 'deleteReply') return 'Are you sure you want to delete this message? This cannot be undone.';
    return '';
  };

  const {
    ticket_id = "",
    reason = "",
    date = "",
    reply = "",
    attachement = "",
    sent_by = "",
    status,
    replies = [],
  } = ticket || {};

  useEffect(() => {
    if (ticket_id) {
      document.title = `TK | TKT-${ticket_id}`;
    }
    return () => { document.title = 'Fronic Media Admin'; };
  }, [ticket_id]);

  const viewAttachment = (attachement && attachement.split(",")) || [];
  const isOpen = status === 0;

  return (
    <Grid container spacing={2.5}>
      {/* ========== Header ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <ActionButton
              variant="info"
              sx={{ py: 0.4, px: 1.5, fontSize: '0.8rem', gap: 0.5 }}
              onClick={() => navigate('/tickets')}
            >
              <ArrowLeft size={14} /> Back
            </ActionButton>
            <Typography sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1.1rem' }}>
              TKT-{ticket_id}
            </Typography>
            <StatusBadge status={isOpen ? 'open' : 'closed'} />
          </Box>
          {isOpen && (
            <ActionButton
              variant="danger"
              sx={{ py: 0.4, px: 2, fontSize: '0.8rem', gap: 0.5 }}
              onClick={onCloseTicket}
            >
              <XCircle size={14} /> Close Ticket
            </ActionButton>
          )}
        </Box>
      </Grid>

      {/* ========== Ticket Info Card ========== */}
      <Grid item xs={12}>
        <Paper sx={{
          backgroundColor: 'var(--bg-paper)',
          border: '1px solid var(--border)',
          borderRadius: '2px',
          borderLeft: `3px solid ${isOpen ? '#10B981' : '#F59E0B'}`,
          p: 2.5,
        }}>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Reason */}
            <Box>
              <Typography sx={{ color: '#64748B', fontSize: '0.7rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                Reason
              </Typography>
              <Typography sx={{ color: '#F1F5F9', fontSize: '0.95rem', fontWeight: 500 }}>
                {reason || '-'}
              </Typography>
            </Box>

            {/* Ticket Message */}
            {reply && (
              <Box>
                <Typography sx={{ color: '#64748B', fontSize: '0.7rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Ticket Message
                </Typography>
                <Typography sx={{ color: '#CBD5E1', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
                  {reply}
                </Typography>
              </Box>
            )}

            {/* Meta row */}
            <Box display="flex" alignItems="center" gap={2.5} flexWrap="wrap">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#64748B', fontSize: '0.85rem' }}>
                <Calendar size={13} /> {date ? moment(date).format('DD/MM/YYYY') : '-'}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#64748B', fontSize: '0.85rem' }}>
                <MessageSquare size={13} /> {replies?.length || 0} Replies
              </span>
              {viewAttachment.length > 0 && viewAttachment[0] && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#64748B', fontSize: '0.85rem' }}>
                  <Paperclip size={13} /> {viewAttachment.length} Attachment{viewAttachment.length > 1 ? 's' : ''}
                </span>
              )}
            </Box>

            {/* Attachments */}
            {viewAttachment.length > 0 && viewAttachment[0] && (
              <Box>
                <Typography sx={{ color: '#64748B', fontSize: '0.7rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                  Attachments
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {viewAttachment.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        backgroundColor: 'var(--bg-elevated)',
                        border: '1px solid var(--border)',
                        borderRadius: '2px',
                      }}
                    >
                      <a
                        href={`${API_URL}/${item}`}
                        download
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6C63FF', textDecoration: 'none', fontSize: '0.8rem' }}
                      >
                        <Download size={13} /> File {index + 1}
                      </a>
                      <Trash2
                        size={13}
                        color="#EF4444"
                        style={{ cursor: 'pointer' }}
                        onClick={() => onDelete(item)}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>

      {/* ========== Quick Reply Select ========== */}
      {isOpen && (
        <Grid item xs={12}>
          <Box sx={{ width: '100%' }}>
            <Typography sx={{ color: '#64748B', fontSize: '0.7rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
              Quick Reply Template
            </Typography>
            <SelectCMP
              options={rejectOptions}
              placeholder="Select a template..."
              onChange={(e) => setSendReply({ ...sendReply, reply: e.value })}
              styles={darkSelectStyles}
            />
          </Box>
        </Grid>
      )}

      {/* ========== Conversation ========== */}
      <Grid item xs={12}>
        <Paper sx={{
          backgroundColor: 'var(--bg-paper)',
          border: '1px solid var(--border)',
          borderRadius: '2px',
          overflow: 'hidden',
        }}>
          {/* Conversation Header */}
          <Box sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <MessageSquare size={15} color="#6C63FF" />
            <Typography sx={{ color: '#F1F5F9', fontSize: '0.9rem', fontWeight: 600 }}>
              Conversation
            </Typography>
            <Chip
              label={replies?.length || 0}
              size="small"
              sx={{
                backgroundColor: 'rgba(108, 99, 255, 0.12)',
                color: '#6C63FF',
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 22,
                minWidth: 28,
              }}
            />
          </Box>

          {/* Messages */}
          <Box sx={{ px: 2.5, py: 2, maxHeight: 500, overflowY: 'auto' }}>
            {replies && replies.length ? (
              replies.map((item, key) => {
                const isAdmin = item.sent_by === "admin";
                const isEditing = editingReply === item.reply_id;
                return (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                      mb: 2,
                      pb: 2,
                      borderBottom: key < replies.length - 1 ? '1px solid rgba(255, 255, 255, 0.15)' : 'none',
                    }}
                  >
                    <Box sx={{
                      maxWidth: isEditing ? '55vw' : '70%',
                      minWidth: isEditing ? '55vw' : 'auto',
                      px: 2,
                      py: 1.5,
                      borderRadius: '2px',
                      backgroundColor: isAdmin ? 'rgba(108, 99, 255, 0.1)' : 'var(--bg-elevated)',
                      border: `1px solid ${isAdmin ? 'rgba(108, 99, 255, 0.2)' : 'var(--border)'}`,
                      borderLeft: isAdmin ? 'none' : '3px solid #64748B',
                      borderRight: isAdmin ? '3px solid #6C63FF' : 'none',
                    }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography sx={{
                          color: isAdmin ? '#A78BFA' : '#94A3B8',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          mb: 0.5,
                        }}>
                          {isAdmin ? 'Admin' : 'User'}
                        </Typography>
                        {isAdmin && !isEditing && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <ActionButton
                              variant="info"
                              sx={{ py: 0.2, px: 1.5, fontSize: '0.7rem', gap: 0.5 }}
                              onClick={() => { setEditingReply(item.reply_id); setEditingText(item.reply); }}
                            >
                              Edit
                            </ActionButton>
                            <IconButton
                              size="small"
                              onClick={() => onDeleteReply(item.reply_id)}
                              sx={{ color: '#EF4444', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.12)' } }}
                            >
                              <Trash2 size={14} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {isEditing ? (
                        <Box>
                          <textarea
                            rows={4}
                            className="text-area-input"
                            style={{ width: '100%', resize: 'vertical', marginBottom: 8 }}
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                          />
                          <Box display="flex" justifyContent="flex-end" gap={1}>
                            <ActionButton
                              variant="info"
                              sx={{ py: 0.4, px: 2, fontSize: '0.8rem' }}
                              onClick={() => { setEditingReply(null); setEditingText(""); }}
                            >
                              Cancel
                            </ActionButton>
                            <ActionButton
                              variant="success"
                              sx={{ py: 0.4, px: 2, fontSize: '0.8rem' }}
                              onClick={() => onSaveEditReply(item.reply_id)}
                            >
                              Edit
                            </ActionButton>
                          </Box>
                        </Box>
                      ) : (
                        <Typography sx={{ color: '#F1F5F9', fontSize: '0.88rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                          {item.reply}
                        </Typography>
                      )}
                      <Typography sx={{
                        color: '#64748B',
                        fontSize: '0.7rem',
                        mt: 1,
                        textAlign: isAdmin ? 'right' : 'left',
                        fontFamily: 'monospace',
                      }}>
                        {moment(item.created_at).format("DD/MM/YYYY hh:mm A")}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, color: '#64748B' }}>
                <MessageSquare size={28} style={{ marginBottom: 8, opacity: 0.5 }} />
                <Typography sx={{ fontSize: '0.85rem' }}>No replies yet</Typography>
              </Box>
            )}
          </Box>

          {/* Reply Input */}
          {isOpen && (
            <Box sx={{
              px: 2.5,
              py: 2,
              borderTop: '1px solid var(--border)',
              display: 'flex',
              gap: 1.5,
            }}>
              <textarea
                rows={5}
                className="text-area-input"
                style={{ flex: 1, resize: 'vertical' }}
                placeholder="Type your reply... (Enter to send, Shift+Enter for new line)"
                value={sendReply.reply}
                onKeyPress={keyPressed}
                onChange={(e) => setSendReply({ ...sendReply, reply: e.target.value })}
              />
              <ActionButton
                variant="success"
                sx={{ px: 2, alignSelf: 'flex-end', gap: 0.5 }}
                onClick={onSubmitReply}
                disabled={sendLoader || !sendReply.reply?.trim()}
              >
                {sendLoader ? (
                  <CircularProgress size={16} sx={{ color: '#10B981' }} />
                ) : (
                  <>
                    <Send size={14} /> Send
                  </>
                )}
              </ActionButton>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* ========== Confirmation Modal ========== */}
      <CustomShowModal
        handleClose={() => setConfirmModal({ open: false, type: null, data: null })}
        title={getConfirmTitle()}
        open={confirmModal.open}
        maxWidth="sm"
      >
        <Typography sx={{ color: '#94A3B8', fontSize: '0.9rem', mb: 2 }}>
          {getConfirmMessage()}
        </Typography>
        <Box display="flex" justifyContent="flex-end" gap={1} py={1}>
          <ActionButton variant="info" onClick={() => setConfirmModal({ open: false, type: null, data: null })}>
            Cancel
          </ActionButton>
          <ActionButton
            variant={confirmModal.type === 'delete' || confirmModal.type === 'close' || confirmModal.type === 'deleteReply' ? 'danger' : 'success'}
            onClick={onConfirmAction}
          >
            {confirmModal.type === 'reply' ? 'Send' : confirmModal.type === 'close' ? 'Close Ticket' : 'Delete'}
          </ActionButton>
        </Box>
      </CustomShowModal>
    </Grid>
  );
}

export default ViewTicket;
